/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable no-console */
import {
  BackgroundScriptMessageType,
  ContentScriptMessageType,
  SidePanelMessageType,
} from './extension.enums';
import {
  AnyBackgroundScriptMessage,
  AnyContentScriptMessage,
  AnySidePanelMessage,
  BackgroundScriptMessage,
  ContentScriptMessage,
  SidePanelMessage,
} from './messages.types';

type MessageCallback = (
  message: AnyContentScriptMessage | AnySidePanelMessage,
  sender?: browser.runtime.MessageSender | chrome.runtime.MessageSender,
  sendResponse?: any
) => void;

/**
 * Module-level port state for direct side panel <-> content script communication.
 * The background service worker never calls setPort(), so activePort is always null
 * in that context and messaging falls back to chrome.runtime/tabs.sendMessage.
 */
let activePort: chrome.runtime.Port | browser.runtime.Port | null = null;
const portMessageCallbacks = new Set<MessageCallback>();

/**
 * Registers a port for direct messaging between side panel and content script.
 * Sets up an onMessage listener that forwards messages to all tracked callbacks,
 * and clears the port on disconnect.
 */
export const setPort = (port: typeof activePort) => {
  activePort = port;

  if (port) {
    port.onMessage.addListener((message: Parameters<MessageCallback>['0']) => {
      for (const callback of portMessageCallbacks) {
        callback(message, port.sender);
      }
    });

    port.onDisconnect.addListener(() => {
      activePort = null;
    });
  }
};

/** Returns the currently active port, or null if none is set. */
export const getPort = () => activePort;

/**
 * Sends a message to the content script running in a tab.
 * Uses the active port if available (side panel context), otherwise falls back
 * to chrome.tabs.sendMessage (background service worker context).
 *
 * @param message - The message to send
 * @param tabId - Optional tab ID to send to. If not provided, sends to the active tab.
 */
export async function sendMessageToContentScript<
  T extends ContentScriptMessageType,
>(message: ContentScriptMessage<T>, tabId?: number) {
  if (activePort) {
    activePort.postMessage(message);
    console.log(`Message "${message.type}" sent via port`);
    return null;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let response: any = null;

  if (globalThis.ENVS.isFirefoxLike) {
    // Use provided tabId or fall back to querying active tab
    let targetTabId = tabId;
    if (!targetTabId) {
      const tabs = await browser.tabs.query({
        active: true,
        currentWindow: true,
      });
      targetTabId = tabs[0]?.id;
    }

    if (!targetTabId) {
      return null;
    }

    response = await browser.tabs.sendMessage(targetTabId, message);
  } else if (chrome?.tabs?.sendMessage) {
    // Use provided tabId or fall back to querying active tab
    let targetTabId = tabId;
    if (!targetTabId) {
      const tabs = await chrome.tabs.query({
        active: true,
        currentWindow: true,
      });
      targetTabId = tabs[0]?.id;
    }

    if (!targetTabId) {
      return null;
    }

    response = await chrome.tabs.sendMessage(targetTabId, message);
  }

  if (!response) {
    console.log(`Message "${message.type}" sent`);
  } else {
    console.log(`Message "${message.type}" sent with response: `, response);
  }

  return response;
}

/**
 * Sends a message to the side panel.
 * Uses the active port if available (content script context), otherwise falls back
 * to chrome.runtime.sendMessage.
 */
export async function sendMessageToSidePanel<T extends SidePanelMessageType>(
  message: SidePanelMessage<T>
) {
  if (activePort) {
    activePort.postMessage(message);
    console.log(`Message "${message.type}" sent via port`);
    return null;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let response: any = null;

  if (globalThis.ENVS.isFirefoxLike) {
    response = await browser.runtime.sendMessage(message);
  } else if (chrome?.runtime?.sendMessage) {
    response = await chrome.runtime.sendMessage(message);
  }

  if (!response) {
    console.log(`Message "${message.type}" sent`);
  } else {
    console.log(`Message "${message.type}" sent with response: `, response);
  }

  return response;
}

export async function sendMessageToBackground<
  T extends BackgroundScriptMessageType,
>(message: BackgroundScriptMessage<T>) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let response: any = null;

  if (globalThis.ENVS.isFirefoxLike) {
    response = await browser.runtime.sendMessage(message);
  } else if (chrome?.runtime?.sendMessage) {
    response = await chrome.runtime.sendMessage(message);
  }

  if (!response) {
    console.log(`Message "${message.type}" sent`);
  } else {
    console.log(`Message "${message.type}" sent with response: `, response);
  }

  return response;
}

/**
 * Registers a message listener on both the port (for direct side panel <-> content script
 * messages) and chrome.runtime.onMessage (for background script messages and fallback).
 */
export function onMessage<
  T =
    | AnyContentScriptMessage
    | AnySidePanelMessage
    | AnyBackgroundScriptMessage,
>(
  callback: (
    message: T,
    sender: browser.runtime.MessageSender | chrome.runtime.MessageSender,
    sendResponse: (response?: T) => void
  ) => void
) {
  portMessageCallbacks.add(callback as MessageCallback);

  if (globalThis.ENVS.isFirefoxLike) {
    browser.runtime.onMessage.addListener(callback);
  } else if (chrome?.runtime?.onMessage) {
    chrome.runtime.onMessage.addListener(callback);
  }
}

export function removeOnMessage<
  T =
    | AnyContentScriptMessage
    | AnySidePanelMessage
    | AnyBackgroundScriptMessage,
>(
  callback: (
    message: T,
    sender: browser.runtime.MessageSender | chrome.runtime.MessageSender,
    sendResponse: (response?: T) => void
  ) => void
) {
  portMessageCallbacks.delete(callback as MessageCallback);

  if (globalThis.ENVS.isFirefoxLike) {
    browser.runtime.onMessage.removeListener(callback);
  } else if (chrome?.runtime?.onMessage) {
    chrome.runtime.onMessage.removeListener(callback);
  }
}
